/**
 * ðŸŽ¯ SUSTAINABILITY AUDIT - Formulário de Auditoria de Sustentação
 * 
 * Formulário com critérios específicos por conceito Lean,
 * notas de 1 a 4, gráfico de evolução mensal e dashboard.
 * Cores: Vermelho 1-2 | Amarelo 2-3 | Azul 3-4 | Verde 4
 */

import React, { useState, useMemo } from 'react';
import {
  ShieldCheck,
  Star,
  Calendar,
  User,
  MapPin,
  FileText,
  Plus,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle2,
  Clock,
  BarChart3,
  LineChart,
  Info,
} from 'lucide-react';
import {
  SustainabilityAudit,
  AuditCriterion,
  AuditScore,
  DEFAULT_AUDIT_CRITERIA,
  getAuditColor,
  generateId,
  LeanConcept,
  LEAN_CONCEPT_LABELS,
  CONCEPT_AUDIT_CRITERIA,
  ConceptAuditCriterion,
} from '../../types/consultingProject';

interface SustainabilityAuditViewProps {
  audits: SustainabilityAudit[];
  criteria: AuditCriterion[];
  projectId: string;
  onAddAudit: (audit: Partial<SustainabilityAudit>) => void;
  onUpdateAudit: (id: string, updates: Partial<SustainabilityAudit>) => void;
  kaizenOptions?: { id: string; title: string; concept?: LeanConcept }[];
  readOnly?: boolean;
}

const SCORE_LABELS: Record<number, string> = {
  1: 'Crítico',
  2: 'Parcial',
  3: 'Bom',
  4: 'Excelente',
};

// New color scheme: Red 1-2 | Yellow 2-3 | Blue 3-4 | Green 4
const SCORE_COLORS: Record<number, string> = {
  1: '#EF4444', // red
  2: '#F59E0B', // yellow
  3: '#3B82F6', // blue
  4: '#10B981', // green
};

const getScoreColor = (score: number): string => {
  if (score >= 4) return '#10B981';
  if (score >= 3) return '#3B82F6';
  if (score >= 2) return '#F59E0B';
  return '#EF4444';
};

const getScoreBg = (score: number): string => {
  if (score >= 4) return 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800';
  if (score >= 3) return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
  if (score >= 2) return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
  return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
};

export const SustainabilityAuditView: React.FC<SustainabilityAuditViewProps> = ({
  audits,
  criteria,
  projectId,
  onAddAudit,
  onUpdateAudit,
  kaizenOptions = [],
  readOnly = false,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [expandedAudit, setExpandedAudit] = useState<string | null>(null);
  const [selectedConcept, setSelectedConcept] = useState<LeanConcept | ''>('');
  const [showChart, setShowChart] = useState(true);

  const [formData, setFormData] = useState({
    kaizenTitle: '',
    concept: '' as LeanConcept | '',
    auditor: '',
    area: '',
    auditDate: new Date().toISOString().substring(0, 10),
    scores: [] as { criterionId: string; score: number; notes: string }[],
    correctiveActions: '',
    nextAuditDate: '',
  });

  const handleConceptChange = (concept: LeanConcept | '') => {
    setSelectedConcept(concept);
    setFormData(prev => ({
      ...prev,
      concept,
      scores: concept
        ? CONCEPT_AUDIT_CRITERIA[concept].map(c => ({ criterionId: c.id, score: 0, notes: '' }))
        : [],
    }));
  };

  const handleScoreChange = (criterionId: string, score: number) => {
    setFormData(prev => ({
      ...prev,
      scores: prev.scores.map(s =>
        s.criterionId === criterionId ? { ...s, score } : s
      ),
    }));
  };

  const handleScoreNotes = (criterionId: string, notes: string) => {
    setFormData(prev => ({
      ...prev,
      scores: prev.scores.map(s =>
        s.criterionId === criterionId ? { ...s, notes } : s
      ),
    }));
  };

  const handleSubmit = () => {
    if (!formData.kaizenTitle || !formData.auditor || !formData.concept) return;
    const allScored = formData.scores.every(s => s.score > 0);
    if (!allScored) return;

    const totalScore = formData.scores.reduce((sum, s) => sum + s.score, 0);
    const averageScore = Number((totalScore / formData.scores.length).toFixed(1));

    onAddAudit({
      kaizenTitle: formData.kaizenTitle,
      concept: formData.concept as LeanConcept,
      auditor: formData.auditor,
      area: formData.area,
      auditDate: formData.auditDate,
      scores: formData.scores,
      averageScore,
      correctiveActions: formData.correctiveActions,
      nextAuditDate: formData.nextAuditDate,
      status: averageScore >= 3 ? 'completed' : 'action_required',
    });

    setFormData({
      kaizenTitle: '',
      concept: '',
      auditor: '',
      area: '',
      auditDate: new Date().toISOString().substring(0, 10),
      scores: [],
      correctiveActions: '',
      nextAuditDate: '',
    });
    setSelectedConcept('');
    setShowForm(false);
  };

  // Overall average
  const overallAvg = audits.length > 0
    ? Number((audits.reduce((sum, a) => sum + a.averageScore, 0) / audits.length).toFixed(1))
    : 0;

  // Group by kaizen
  const auditsByKaizen = useMemo(() => {
    return audits.reduce((acc, audit) => {
      if (!acc[audit.kaizenTitle]) acc[audit.kaizenTitle] = [];
      acc[audit.kaizenTitle].push(audit);
      return acc;
    }, {} as Record<string, SustainabilityAudit[]>);
  }, [audits]);

  const getTrend = (kaizenAudits: SustainabilityAudit[]) => {
    if (kaizenAudits.length < 2) return 'neutral';
    const sorted = [...kaizenAudits].sort((a, b) => new Date(a.auditDate).getTime() - new Date(b.auditDate).getTime());
    const last = sorted[sorted.length - 1].averageScore;
    const prev = sorted[sorted.length - 2].averageScore;
    if (last > prev) return 'up';
    if (last < prev) return 'down';
    return 'neutral';
  };

  // Chart data
  const chartData = useMemo(() => {
    if (audits.length === 0) return null;
    const monthSet = new Set<string>();
    audits.forEach(a => {
      const d = new Date(a.auditDate);
      monthSet.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    });
    const months = Array.from(monthSet).sort();
    const kaizenTitles = Object.keys(auditsByKaizen);
    const monthlyData = months.map(month => {
      const [year, mon] = month.split('-').map(Number);
      const monthAudits = audits.filter(a => {
        const d = new Date(a.auditDate);
        return d.getFullYear() === year && d.getMonth() + 1 === mon;
      });
      const perKaizen: Record<string, number> = {};
      kaizenTitles.forEach(title => {
        const kAudits = monthAudits.filter(a => a.kaizenTitle === title);
        if (kAudits.length > 0) {
          perKaizen[title] = Number((kAudits.reduce((s, a) => s + a.averageScore, 0) / kAudits.length).toFixed(1));
        }
      });
      const monthAvg = monthAudits.length > 0
        ? Number((monthAudits.reduce((s, a) => s + a.averageScore, 0) / monthAudits.length).toFixed(1))
        : 0;
      return {
        month,
        label: new Date(year, mon - 1).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
        perKaizen,
        average: monthAvg,
        count: monthAudits.length,
      };
    });
    return { months: monthlyData, kaizenTitles };
  }, [audits, auditsByKaizen]);

  const kaizenAuditCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    Object.entries(auditsByKaizen).forEach(([title, ka]) => {
      counts[title] = ka.length;
    });
    return counts;
  }, [auditsByKaizen]);

  const KAIZEN_COLORS = [
    '#8B5CF6', '#EC4899', '#06B6D4', '#F97316', '#84CC16',
    '#6366F1', '#14B8A6', '#F43F5E', '#A855F7', '#EAB308',
  ];

  // Evolution chart
  const renderEvolutionChart = () => {
    if (!chartData || chartData.months.length === 0) return null;
    const { months, kaizenTitles } = chartData;
    const chartWidth = Math.max(600, months.length * 120);
    const chartHeight = 240;
    const pl = 40, pr = 20, pt = 20, pb = 40;
    const pw = chartWidth - pl - pr;
    const ph = chartHeight - pt - pb;
    const getX = (idx: number) => pl + (idx / Math.max(months.length - 1, 1)) * pw;
    const getY = (val: number) => pt + ph - ((val - 1) / 3) * ph;

    return (
      <div className="bg-white dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <LineChart size={16} className="text-blue-600" />
            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300">Evolução das Auditorias</h3>
          </div>
          <div className="flex items-center gap-3 text-[10px]">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-500" /> 1-2</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-yellow-500" /> 2-3</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-500" /> 3-4</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500" /> 4</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <svg width={chartWidth} height={chartHeight} className="w-full" viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
            <rect x={pl} y={getY(2)} width={pw} height={getY(1) - getY(2)} fill="#EF444410" />
            <rect x={pl} y={getY(3)} width={pw} height={getY(2) - getY(3)} fill="#F59E0B10" />
            <rect x={pl} y={getY(4)} width={pw} height={getY(3) - getY(4)} fill="#3B82F610" />
            {[1, 2, 3, 4].map(val => (
              <g key={val}>
                <line x1={pl} y1={getY(val)} x2={chartWidth - pr} y2={getY(val)} stroke="#E2E8F0" strokeWidth={1} strokeDasharray={val === 1 || val === 4 ? '0' : '4,4'} />
                <text x={pl - 8} y={getY(val) + 4} textAnchor="end" fontSize={10} fill="#94A3B8">{val}</text>
              </g>
            ))}
            {months.map((m, i) => (
              <text key={m.month} x={getX(i)} y={chartHeight - 10} textAnchor="middle" fontSize={10} fill="#94A3B8">{m.label}</text>
            ))}
            {kaizenTitles.map((title, kIdx) => {
              const color = KAIZEN_COLORS[kIdx % KAIZEN_COLORS.length];
              const points: { x: number; y: number; val: number }[] = [];
              months.forEach((m, mIdx) => {
                if (m.perKaizen[title] !== undefined) {
                  points.push({ x: getX(mIdx), y: getY(m.perKaizen[title]), val: m.perKaizen[title] });
                }
              });
              if (points.length === 0) return null;
              const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
              return (
                <g key={title}>
                  <path d={pathD} fill="none" stroke={color} strokeWidth={1.5} opacity={0.6} />
                  {points.map((p, i) => (
                    <g key={i}><circle cx={p.x} cy={p.y} r={3} fill={color} opacity={0.7} /><title>{`${title}: ${p.val}`}</title></g>
                  ))}
                </g>
              );
            })}
            {months.length > 0 && (() => {
              const avgPoints = months.filter(m => m.average > 0).map(m => ({
                x: getX(months.indexOf(m)), y: getY(m.average), val: m.average,
              }));
              if (avgPoints.length === 0) return null;
              const avgPathD = avgPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
              return (
                <g>
                  <path d={avgPathD} fill="none" stroke="#1E293B" strokeWidth={2.5} strokeDasharray="6,3" />
                  {avgPoints.map((p, i) => (
                    <g key={i}>
                      <circle cx={p.x} cy={p.y} r={5} fill="#1E293B" stroke="white" strokeWidth={2} />
                      <text x={p.x} y={p.y - 10} textAnchor="middle" fontSize={10} fontWeight="bold" fill="#1E293B">{p.val}</text>
                    </g>
                  ))}
                </g>
              );
            })()}
          </svg>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-[10px] border-t border-slate-100 dark:border-slate-700 pt-3">
          <span className="flex items-center gap-1.5 font-semibold text-slate-600 dark:text-slate-400">
            <span className="w-5 h-0.5" style={{ borderTop: '2.5px dashed #1E293B' }} /> Média Geral
          </span>
          {kaizenTitles.map((title, i) => (
            <span key={title} className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: KAIZEN_COLORS[i % KAIZEN_COLORS.length] }} />
              {title} <span className="text-slate-400">({kaizenAuditCounts[title]}x)</span>
            </span>
          ))}
        </div>
      </div>
    );
  };

  // Audit form
  const renderAuditForm = () => {
    const activeConcept = formData.concept as LeanConcept;
    const conceptCriteriaList = activeConcept ? CONCEPT_AUDIT_CRITERIA[activeConcept] : [];

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-white dark:bg-slate-800 rounded shadow-2xl w-full max-w-2xl p-6 mx-4 max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2">
            <ShieldCheck size={20} className="text-blue-600" />
            Nova Auditoria de Sustentação
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Kaizen Auditado *</label>
                {kaizenOptions.length > 0 ? (
                  <select
                    value={formData.kaizenTitle}
                    onChange={e => {
                      const sel = kaizenOptions.find(k => k.title === e.target.value);
                      setFormData(f => ({ ...f, kaizenTitle: e.target.value }));
                      if (sel?.concept) {
                        handleConceptChange(sel.concept);
                        setFormData(f => ({ ...f, kaizenTitle: e.target.value, concept: sel.concept! }));
                      }
                    }}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                  >
                    <option value="">Selecione...</option>
                    {kaizenOptions.map(k => (
                      <option key={k.id} value={k.title}>{k.title}</option>
                    ))}
                  </select>
                ) : (
                  <input type="text" value={formData.kaizenTitle}
                    onChange={e => setFormData(f => ({ ...f, kaizenTitle: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                    placeholder="Nome do kaizen auditado" />
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Conceito Lean *</label>
                <select value={formData.concept}
                  onChange={e => handleConceptChange(e.target.value as LeanConcept | '')}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200">
                  <option value="">Selecione o conceito...</option>
                  {Object.entries(LEAN_CONCEPT_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            {formData.kaizenTitle && auditsByKaizen[formData.kaizenTitle] && (
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-400">
                  <Info size={13} />
                  Esta será a <strong>{auditsByKaizen[formData.kaizenTitle].length + 1}Âª auditoria</strong> deste kaizen.
                  {auditsByKaizen[formData.kaizenTitle].length > 0 && (
                    <span>Ãšltima nota: <strong>{[...auditsByKaizen[formData.kaizenTitle]]
                      .sort((a, b) => new Date(b.auditDate).getTime() - new Date(a.auditDate).getTime())[0].averageScore}</strong></span>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Auditor *</label>
                <input type="text" value={formData.auditor}
                  onChange={e => setFormData(f => ({ ...f, auditor: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Área</label>
                <input type="text" value={formData.area}
                  onChange={e => setFormData(f => ({ ...f, area: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Data</label>
                <input type="date" value={formData.auditDate}
                  onChange={e => setFormData(f => ({ ...f, auditDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200" />
              </div>
            </div>

            {formData.concept && conceptCriteriaList.length > 0 ? (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                    Avaliação â€” {LEAN_CONCEPT_LABELS[formData.concept as LeanConcept]}
                  </label>
                  <span className="text-[10px] px-2 py-0.5 rounded-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium">
                    Selecione a frase que melhor descreve a situação
                  </span>
                </div>
                <div className="space-y-4">
                  {conceptCriteriaList.map(criterion => {
                    const currentScore = formData.scores.find(s => s.criterionId === criterion.id);
                    return (
                      <div key={criterion.id}
                        className={`p-4 rounded border transition-all ${
                          currentScore && currentScore.score > 0
                            ? getScoreBg(currentScore.score)
                            : 'bg-slate-50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600'
                        }`}>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300">{criterion.order}. {criterion.name}</h4>
                          {currentScore && currentScore.score > 0 && (
                            <span className="text-xs font-bold px-2.5 py-0.5 rounded-sm text-white"
                              style={{ backgroundColor: getScoreColor(currentScore.score) }}>
                              {currentScore.score} â€” {SCORE_LABELS[currentScore.score]}
                            </span>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          {criterion.phrases.map(phrase => {
                            const isSelected = currentScore?.score === phrase.score;
                            const color = SCORE_COLORS[phrase.score];
                            return (
                              <button key={phrase.score}
                                onClick={() => handleScoreChange(criterion.id, phrase.score)}
                                className={`w-full text-left px-3 py-2 rounded text-xs transition-all border ${
                                  isSelected ? 'text-white shadow-md font-semibold'
                                    : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-600 hover:border-slate-300'
                                }`}
                                style={isSelected ? { backgroundColor: color, borderColor: color } : {}}>
                                <div className="flex items-start gap-2">
                                  <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5 ${
                                    isSelected ? 'bg-white/30 text-white' : 'border-2'
                                  }`} style={!isSelected ? { borderColor: color, color } : {}}>
                                    {phrase.score}
                                  </span>
                                  <span className="flex-1">{phrase.description}</span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        <input type="text" value={currentScore?.notes || ''}
                          onChange={e => handleScoreNotes(criterion.id, e.target.value)}
                          className="w-full mt-2 px-2 py-1 border border-slate-200 dark:border-slate-600 rounded text-[11px] bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400"
                          placeholder="Observação (opcional)" />
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-slate-400 dark:text-slate-600">
                <Info size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-xs">Selecione o conceito Lean para exibir os critérios de avaliação</p>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Ações Corretivas</label>
              <textarea value={formData.correctiveActions}
                onChange={e => setFormData(f => ({ ...f, correctiveActions: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 resize-none"
                placeholder="Descreva as ações necessárias caso a nota esteja abaixo do esperado" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Próxima Auditoria</label>
              <input type="date" value={formData.nextAuditDate}
                onChange={e => setFormData(f => ({ ...f, nextAuditDate: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => { setShowForm(false); setSelectedConcept(''); }}
              className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors">
              Cancelar
            </button>
            <button onClick={handleSubmit}
              disabled={!formData.kaizenTitle || !formData.auditor || !formData.concept || formData.scores.some(s => s.score === 0)}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 font-semibold">
              Salvar Auditoria
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700 p-4 text-center">
          <div className="text-2xl font-bold text-slate-800 dark:text-slate-200">{audits.length}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Auditorias</div>
        </div>
        <div className="bg-white dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700 p-4 text-center">
          <div className="text-2xl font-bold" style={{ color: getScoreColor(overallAvg) }}>{overallAvg || '-'}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Média Geral</div>
        </div>
        <div className="bg-white dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700 p-4 text-center">
          <div className="text-2xl font-bold text-emerald-600">{audits.filter(a => a.averageScore >= 3).length}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Sustentados</div>
        </div>
        <div className="bg-white dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700 p-4 text-center">
          <div className="text-2xl font-bold text-red-500">{audits.filter(a => a.status === 'action_required').length}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Ação Requerida</div>
        </div>
      </div>

      {/* Color legend bar */}
      <div className="flex items-center gap-1 p-2 bg-white dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700">
        <div className="flex-1 h-2 rounded-l-full bg-red-500" title="1-2: Crítico" />
        <div className="flex-1 h-2 bg-yellow-500" title="2-3: Parcial" />
        <div className="flex-1 h-2 bg-blue-500" title="3-4: Bom" />
        <div className="flex-1 h-2 rounded-r-full bg-emerald-500" title="4: Excelente" />
        <div className="flex items-center gap-3 ml-3 text-[10px] text-slate-500 dark:text-slate-400">
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-full" />1-2</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-yellow-500 rounded-full" />2-3</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-500 rounded-full" />3-4</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-full" />4</span>
        </div>
      </div>

      {/* Evolution Chart */}
      {audits.length > 0 && (
        <div>
          <button onClick={() => setShowChart(!showChart)}
            className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2 hover:text-blue-600 transition-colors">
            <BarChart3 size={14} /> Gráfico de Evolução
            {showChart ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          {showChart && renderEvolutionChart()}
        </div>
      )}

      {/* Kaizen average table */}
      {Object.keys(auditsByKaizen).length > 0 && (
        <div className="bg-white dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700 p-5">
          <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
            <BarChart3 size={14} /> Média por Kaizen
          </h3>
          <div className="space-y-2">
            {Object.entries(auditsByKaizen)
              .map(([title, ka]) => ({
                title,
                count: ka.length,
                avg: Number((ka.reduce((s, a) => s + a.averageScore, 0) / ka.length).toFixed(1)),
                concept: ka[0]?.concept,
                lastDate: [...ka].sort((a, b) => new Date(b.auditDate).getTime() - new Date(a.auditDate).getTime())[0]?.auditDate,
              }))
              .sort((a, b) => b.avg - a.avg)
              .map(k => (
                <div key={k.title} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                  <div className="w-8 h-8 rounded flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                    style={{ backgroundColor: getScoreColor(k.avg) }}>{k.avg}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate">{k.title}</div>
                    <div className="text-[10px] text-slate-400 flex items-center gap-2">
                      {k.concept && <span className="text-blue-500">{LEAN_CONCEPT_LABELS[k.concept]}</span>}
                      <span>{k.count} auditoria{k.count !== 1 ? 's' : ''}</span>
                      {k.lastDate && <span>Â· Ãšltima: {new Date(k.lastDate).toLocaleDateString('pt-BR')}</span>}
                    </div>
                  </div>
                  <div className="flex items-center h-4 w-32 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                      style={{ width: `${(k.avg / 4) * 100}%`, backgroundColor: getScoreColor(k.avg) }} />
                  </div>
                </div>
              ))}
          </div>
          <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <span className="text-xs font-bold text-slate-600 dark:text-slate-400">Média Geral</span>
            <span className="text-lg font-bold" style={{ color: getScoreColor(overallAvg) }}>{overallAvg}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300">Histórico de Auditorias</h3>
        {!readOnly && (
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors">
            <Plus size={14} /> Nova Auditoria
          </button>
        )}
      </div>

      {/* Audits by Kaizen */}
      {Object.keys(auditsByKaizen).length === 0 ? (
        <div className="py-12 text-center text-slate-400 dark:text-slate-600">
          <ShieldCheck size={40} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">Nenhuma auditoria realizada</p>
          <p className="text-xs mt-1">Realize auditorias mensais para monitorar a sustentação dos kaizens</p>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(auditsByKaizen).map(([kaizenTitle, kaizenAudits]) => {
            const lastAudit = [...kaizenAudits].sort(
              (a, b) => new Date(b.auditDate).getTime() - new Date(a.auditDate).getTime()
            )[0];
            const trend = getTrend(kaizenAudits);
            const kaizenConcept = lastAudit.concept;

            return (
              <div key={kaizenTitle}
                className="bg-white dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700 overflow-hidden">
                <button
                  onClick={() => setExpandedAudit(expandedAudit === kaizenTitle ? null : kaizenTitle)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded flex items-center justify-center text-white font-bold text-sm"
                      style={{ backgroundColor: getScoreColor(lastAudit.averageScore) }}>
                      {lastAudit.averageScore}
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">{kaizenTitle}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2">
                        {kaizenConcept && <span className="text-blue-500 font-medium">{LEAN_CONCEPT_LABELS[kaizenConcept]}</span>}
                        <span>{kaizenAudits.length} auditoria{kaizenAudits.length !== 1 ? 's' : ''} Â· Ãšltima: {new Date(lastAudit.auditDate).toLocaleDateString('pt-BR')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {trend === 'up' && <TrendingUp size={16} className="text-emerald-500" />}
                    {trend === 'down' && <TrendingDown size={16} className="text-red-500" />}
                    {trend === 'neutral' && <Minus size={16} className="text-slate-400" />}
                    {lastAudit.status === 'action_required' && <AlertTriangle size={14} className="text-amber-500" />}
                    {expandedAudit === kaizenTitle ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                  </div>
                </button>

                {expandedAudit === kaizenTitle && (
                  <div className="border-t border-slate-100 dark:border-slate-700">
                    {kaizenAudits
                      .sort((a, b) => new Date(b.auditDate).getTime() - new Date(a.auditDate).getTime())
                      .map((audit, auditIdx) => {
                        const auditNumber = kaizenAudits.length - auditIdx;
                        const conceptCriteriaForAudit = audit.concept ? CONCEPT_AUDIT_CRITERIA[audit.concept] : [];

                        return (
                          <div key={audit.id} className="px-4 py-3 border-b border-slate-50 dark:border-slate-700/50 last:border-0">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                <span className="font-bold text-slate-700 dark:text-slate-300">#{auditNumber}</span>
                                <Calendar size={12} />
                                {new Date(audit.auditDate).toLocaleDateString('pt-BR')}
                                <span className="mx-1">Â·</span>
                                <User size={12} /> {audit.auditor}
                                {audit.area && (<><span className="mx-1">Â·</span><MapPin size={12} /> {audit.area}</>)}
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] px-2 py-0.5 rounded-sm text-white font-bold"
                                  style={{ backgroundColor: getScoreColor(audit.averageScore) }}>{audit.averageScore}</span>
                                {audit.status === 'completed' && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-sm bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 font-medium">Sustentado</span>
                                )}
                                {audit.status === 'action_required' && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-sm bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-medium">Ação Requerida</span>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-4 gap-2 mt-2">
                              {audit.scores.map(score => {
                                const conceptCrit = conceptCriteriaForAudit.find(c => c.id === score.criterionId);
                                const genericCrit = criteria.find(c => c.id === score.criterionId) || DEFAULT_AUDIT_CRITERIA.find(c => c.id === score.criterionId);
                                const critName = conceptCrit?.name || genericCrit?.name || score.criterionId;
                                const phrase = conceptCrit?.phrases.find(p => p.score === score.score);

                                return (
                                  <div key={score.criterionId} className="text-center p-2 rounded"
                                    style={{ backgroundColor: `${getScoreColor(score.score)}10` }}>
                                    <div className="text-[10px] text-slate-500 dark:text-slate-400 mb-1 truncate font-medium" title={critName}>
                                      {critName}
                                    </div>
                                    <div className="flex justify-center gap-0.5 mb-0.5">
                                      {[1, 2, 3, 4].map(n => (
                                        <Star key={n} size={12}
                                          className={n <= score.score ? '' : 'text-slate-200 dark:text-slate-600'}
                                          fill={n <= score.score ? getScoreColor(score.score) : 'none'}
                                          color={n <= score.score ? getScoreColor(score.score) : undefined} />
                                      ))}
                                    </div>
                                    <div className="text-[9px] font-semibold" style={{ color: getScoreColor(score.score) }}>
                                      {SCORE_LABELS[score.score] || '-'}
                                    </div>
                                    {phrase && (
                                      <div className="text-[9px] text-slate-400 mt-0.5 line-clamp-2" title={phrase.description}>
                                        {phrase.description}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            {audit.correctiveActions && (
                              <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/10 rounded">
                                <div className="text-[10px] font-medium text-amber-700 dark:text-amber-400 mb-0.5">Ações Corretivas:</div>
                                <p className="text-xs text-amber-600 dark:text-amber-300">{audit.correctiveActions}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}

                    {kaizenAudits.length > 1 && (
                      <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800">
                        <div className="text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-2">Evolução da Nota</div>
                        <div className="flex items-end gap-1 h-12">
                          {[...kaizenAudits]
                            .sort((a, b) => new Date(a.auditDate).getTime() - new Date(b.auditDate).getTime())
                            .map(audit => (
                              <div key={audit.id} className="flex-1 rounded-t-sm transition-all"
                                style={{ height: `${(audit.averageScore / 4) * 100}%`, backgroundColor: getScoreColor(audit.averageScore), minWidth: '8px' }}
                                title={`${new Date(audit.auditDate).toLocaleDateString('pt-BR')}: ${audit.averageScore}`} />
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showForm && renderAuditForm()}
    </div>
  );
};
